import { useEffect, useState, useCallback } from 'react';
import TablePagination from '@mui/material/TablePagination';
import { Box, Typography, TextField, Button, IconButton, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCheckPermissions } from '../../utils/roles/permissionUtils';
import { showErrorSnackbar, showSuccessSnackbar } from '../../utils/Snackbar';
import { CommHandler } from '../../utils';
import { config } from '../../config';
import { getAuthHeader } from '../../utils/utils';
import ViewFormDialog from './ViewFormDialog';
import { getFormById } from '../../services/FormService';
import SlabMapping from '../SlabMapping/SlabMapping';
import { truncateText } from '../../common/helper';

// Custom style to target the specific MuiBox-root class
const StyledEvaluation = styled('div')({
  '& .css-1qbzda6': {
    padding: '0px 20px 20px 20px',
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`evaluation-tabpanel-${index}`} aria-labelledby={`evaluation-tab-${index}`} {...other}>
      {value === index && (
        <Box className="css-1qbzda6" sx={{ mt: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

TabPanel.defaultProps = {
  children: undefined,
};

function a11yProps(index: number) {
  return {
    id: `evaluation-tab-${index}`,
    'aria-controls': `evaluation-tabpanel-${index}`,
  };
}

// Define interfaces for API data
interface DesignationData {
  id: number;
  groupName: string;
  originalName?: string;
  level: string;
  department: string;
  designations: string;
  updatedAt: string;
}

interface FormData {
  id: number;
  name: string;
  category: string;
  categoryId?: number; // Add categoryId field
  designationGroup: string;
  questions: number;
  updatedAt: string;
}

// Define sort direction type
type SortDirection = 'asc' | 'desc';

// Define column type for sorting
type DesignationColumnKey = keyof DesignationData;
type FormColumnKey = keyof FormData;
type ColumnKey = DesignationColumnKey | FormColumnKey;

// Sort icon component
const SortIcon = ({ column, sortColumn, sortDirection }: { column: ColumnKey; sortColumn: ColumnKey; sortDirection: SortDirection }) => {
  const active = sortColumn === column;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        ml: 0.5,
        color: active ? '#1976d2' : '#bdbdbd',
        '& svg': {
          fontSize: '18px',
          opacity: active ? 1 : 0.5,
        },
      }}
    >
      {sortDirection === 'asc' && active ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 14l5-5 5 5H7z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5H7z" />
        </svg>
      )}
    </Box>
  );
};

function isDesignationColumn(column: ColumnKey): column is keyof DesignationData {
  return column === 'id' || column === 'groupName' || column === 'originalName' || column === 'level' || column === 'department' || column === 'designations' || column === 'updatedAt';
}

function isFormColumn(column: ColumnKey): column is keyof FormData {
  return column === 'id' || column === 'name' || column === 'category' || column === 'designationGroup' || column === 'questions' || column === 'updatedAt';
}

const Evaluation: React.FC = () => {
  const location = useLocation();
  const defaultTab = location.state?.tabIndex ?? 0;
  const [tabValue, setTabValue] = useState(defaultTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [designationData, setDesignationData] = useState<DesignationData[]>([]);
  const [formData, setFormData] = useState<FormData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<ColumnKey>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state variables for each tab
  const [designationPage, setDesignationPage] = useState(0);
  const [designationRowsPerPage, setDesignationRowsPerPage] = useState(10);
  const [designationTotalCount, setDesignationTotalCount] = useState(0);

  const hasDGAddPermission = useCheckPermissions('ADDESGROUP', ['Write']);
  const hasDGDeletePermission = useCheckPermissions('ADDESGROUP', ['Delete']);
  const hasDGEditPermission = useCheckPermissions('ADDESGROUP', ['Edit']);
  const hasFormAddPermission = useCheckPermissions('ADFORM', ['Write']);
  const hasFormDeletePermission = useCheckPermissions('ADFORM', ['Delete']);
  const hasFormEditPermission = useCheckPermissions('ADFORM', ['Edit']);
  const hasSlabMappingPermission = useCheckPermissions('ADSLM', ['Read', 'Write', 'Edit', 'Delete']);

  const [formPage, setFormPage] = useState(0);
  const [formRowsPerPage, setFormRowsPerPage] = useState(10);
  const [formTotalCount, setFormTotalCount] = useState(0);

  // Dialog states
  const [deleteFormDialog, setDeleteFormDialog] = useState(false);
  const [deleteDesignationDialog, setDeleteDesignationDialog] = useState(false);
  const [viewFormDialog, setViewFormDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [viewFormData, setViewFormData] = useState<any>(null);

  const navigate = useNavigate();

  // UseCallback to ensure stable reference
  const fetchDesignationData = useCallback(async () => {
    try {
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/evaluation/designation-group?page=${designationPage + 1}&limit=${designationRowsPerPage}`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response?.status === 200) {
        const dataArray = response?.data?.data || response?.data || [];
        const totalItems = response?.data?.pagination?.total || dataArray.length || 0;

        setDesignationTotalCount(totalItems);

        const formattedData = dataArray.map((item: any) => {
          const departmentNames = item?.departments ? item?.departments.map((dept: any) => dept?.department_name || dept?.department).join(', ') : 'N/A';

          // Count unique designations across all selected departments
          let totalDesignations = 0;
          const uniqueDesignationIds = new Set<string>();
          if (item?.departments) {
            item.departments.forEach((dept: any) => {
              if (dept?.designations) {
                dept.designations.forEach((d: any) => {
                  const id = String(d?.id || d);
                  if (!uniqueDesignationIds.has(id)) {
                    uniqueDesignationIds.add(id);
                  }
                });
              }
            });
            totalDesignations = uniqueDesignationIds.size;
          }

          // Combine level and name for display
          const level = item?.level || '';
          const name = item?.name || '';
          let combinedGroupName = '';

          if (level) {
            combinedGroupName = `L${level}`;
            if (name) {
              combinedGroupName += `-${name}`;
            }
          } else if (name) {
            combinedGroupName = name;
          } else {
            combinedGroupName = 'N/A';
          }

          return {
            id: item?.id,
            groupName: combinedGroupName,
            originalName: item?.name || '',
            level: item?.level || 'N/A',
            department: departmentNames,
            designations: `${totalDesignations}`,
            updatedAt: item?.updatedAt || new Date().toISOString(),
          };
        });

        // Sort data by updated date descending by default
        const sortedData = [...formattedData].sort((a, b) => {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return dateB - dateA; // descending order
        });

        setDesignationData(sortedData);
      } else {
        showErrorSnackbar('Failed to load designation data');
      }
    } catch (error) {
      showErrorSnackbar('Error fetching designation data');
    }
  }, [designationPage, designationRowsPerPage]);

  const fetchFormData = useCallback(async () => {
    try {
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/evaluation/form?page=${formPage + 1}&limit=${formRowsPerPage}`,
        method: 'GET',
        headers: getAuthHeader,
      });

      if (response.status === 200) {
        const rows: Array<{
          id: number;
          name: string;
          designationGroup?: { name: string; id?: number; level?: string };
          category?: { name: string; id?: number };
          section_title: string;
          question_title: string;
          rating_title: string;
          comment_title: string;
          area_of_improvement: string;
          feedback: string;
          createdAt: string;
          updatedAt: string;
          sections_with_questions?: Array<{
            section_id: string;
            question_ids: number[];
          }>;
          designations: Array<{
            id: number;
            name: string;
            department?: { name: string };
          }>;
        }> = response.data.rows || [];

        const totalItems = response.data.count || rows.length || 0;
        setFormTotalCount(totalItems);

        // If the API doesn't include level information, fetch it separately
        const rowsWithLevel = await Promise.all(
          rows.map(async (rowItem) => {
            const updatedItem = { ...rowItem };
            if (updatedItem.designationGroup && updatedItem.designationGroup.id && !updatedItem.designationGroup.level) {
              try {
                const dgResponse = await CommHandler.request({
                  url: `${config.apiBaseUrl}/evaluation/designation-group/${updatedItem.designationGroup.id}`,
                  method: 'GET',
                  headers: getAuthHeader,
                });

                if (dgResponse.status === 200) {
                  updatedItem.designationGroup = {
                    ...updatedItem.designationGroup,
                    level: dgResponse.data.level || '',
                  };
                }
              } catch (error) {
                // If fetching designation group details fails, continue without level
                console.warn('Failed to fetch designation group level:', error);
              }
            }
            return updatedItem;
          }),
        );

        const formattedData = rowsWithLevel.map((rowItem) => {
          // Format designation group display name with level:name format
          let designationGroupDisplay = '';
          if (rowItem.designationGroup) {
            const level = rowItem.designationGroup.level || '';
            const name = rowItem.designationGroup.name || '';

            if (level) {
              designationGroupDisplay = `L${level}`;
              if (name) {
                designationGroupDisplay += `:${name}`;
              }
            } else if (name) {
              designationGroupDisplay = name;
            } else {
              designationGroupDisplay = 'N/A';
            }
          } else {
            designationGroupDisplay = 'N/A';
          }

          return {
            id: rowItem.id,
            name: rowItem.name,
            designationGroup: designationGroupDisplay,
            category: rowItem.category?.name || '',
            categoryId: rowItem.category?.id, // Add category ID
            sectionTitle: rowItem.section_title,
            questionTitle: rowItem.question_title,
            ratingTitle: rowItem.rating_title,
            commentTitle: rowItem.comment_title,
            areaOfImprovement: rowItem.area_of_improvement,
            feedback: rowItem.feedback,
            createdAt: rowItem.createdAt,
            updatedAt: rowItem.updatedAt,
            questions: rowItem.sections_with_questions ? rowItem.sections_with_questions.reduce((total, section) => total + section.question_ids.length, 0) : 0,
            designations: rowItem.designations.map((designation) => ({
              id: designation.id,
              name: designation.name,
              department: designation.department?.name || '',
            })),
          };
        });

        setFormData(formattedData);
      } else {
        showErrorSnackbar('Failed to load form data');
      }
    } catch (error) {
      showErrorSnackbar('Error fetching form data');
    }
  }, [formPage, formRowsPerPage]);

  const fetchData = useCallback(
    async (tabIndex: number) => {
      setIsLoading(true);
      try {
        if (tabIndex === 0) {
          // Fetch designation data
          await fetchDesignationData();
        } else if (tabIndex === 2) {
          // Fetch form data
          await fetchFormData();
        }
      } catch (error) {
        showErrorSnackbar('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDesignationData, fetchFormData],
  );

  useEffect(() => {
    const title = document.querySelector('title');
    if (title) {
      title.innerText = 'Evaluation';
    }

    // Fetch data based on active tab
    fetchData(tabValue);
  }, [tabValue, designationPage, designationRowsPerPage, formPage, formRowsPerPage, fetchData]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await CommHandler.request({
          url: `${config.apiBaseUrl}/section`,
          method: 'GET',
          headers: getAuthHeader,
        });

        if (response?.status === 200) {
          // Sections fetched but not stored in state as it's unused
          // const formattedSections = response?.data?.map((section: any) => ({
          //   id: section?.id,
          //   name: section?.name,
          // }));
        } else {
          showErrorSnackbar('Failed to load sections');
        }
      } catch (err) {
        showErrorSnackbar('Error loading sections');
      }
    };

    fetchSections();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Patch the reference for the handler in TabPanel
  (TabPanel as any).fetchDesignationData = fetchDesignationData;

  const handleSort = (column: ColumnKey) => {
    // Only allow sorting by groupName in the designations tab
    if (tabValue === 0 && column !== 'groupName') {
      return;
    }

    const isAsc = sortColumn === column && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortColumn(column);

    if (tabValue === 0) {
      // Sort designation data
      const sorted = [...designationData].sort((a, b) => {
        // For date comparison (updatedAt column)
        if (column === 'updatedAt') {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return (isAsc ? 1 : -1) * (dateB - dateA);
        }

        // Handle specifically columns that exist in DesignationData
        if (isDesignationColumn(column)) {
          const valueA = a[column];
          const valueB = b[column];

          // For string comparison
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return (isAsc ? 1 : -1) * valueA.localeCompare(valueB);
          }
        }

        return 0;
      });

      setDesignationData(sorted);
    } else if (tabValue === 2) {
      // Sort form data
      const sorted = [...formData].sort((a, b) => {
        // For date comparison (updatedAt column)
        if (column === 'updatedAt') {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return (isAsc ? 1 : -1) * (dateB - dateA);
        }

        // Map column names properly for Forms tab
        let formColumn: keyof FormData;
        if (column === 'groupName') {
          formColumn = 'designationGroup';
        } else if (isFormColumn(column)) {
          formColumn = column;
        } else {
          return 0; // Skip if the column doesn't exist in FormData
        }

        const valueA = a[formColumn];
        const valueB = b[formColumn];

        // For number comparison (questions)
        if (formColumn === 'questions') {
          return (isAsc ? 1 : -1) * (Number(valueA) - Number(valueB));
        }

        // For string comparison
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return (isAsc ? 1 : -1) * valueA.localeCompare(valueB);
        }

        return 0;
      });

      setFormData(sorted);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const getAddButtonText = () => {
    switch (tabValue) {
      case 0:
        return 'Add Designation Group';
      case 2:
        return 'Add Form';
      default:
        return 'Add';
    }
  };

  const handleAddClick = () => {
    switch (tabValue) {
      case 0:
        navigate('/evaluation/designation/create');
        break;
      case 2:
        navigate('/evaluation/add-form');
        break;
      default:
        break;
    }
  };

  // Filter data based on search query
  const filteredDesignationData = designationData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.groupName || '')
        .toLowerCase()
        .includes(query) ||
      String(item.originalName || '')
        .toLowerCase()
        .includes(query) ||
      String(item.department || '')
        .toLowerCase()
        .includes(query) ||
      String(item.level || '')
        .toLowerCase()
        .includes(query) ||
      String(item.designations || '')
        .toLowerCase()
        .includes(query) ||
      (item.updatedAt && new Date(item.updatedAt).toLocaleDateString().toLowerCase().includes(query))
    );
  });

  const filteredFormData = formData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.name || '')
        .toLowerCase()
        .includes(query) ||
      String(item.category || '')
        .toLowerCase()
        .includes(query) ||
      String(item.designationGroup || '')
        .toLowerCase()
        .includes(query) ||
      item.questions?.toString().includes(query) ||
      (item.updatedAt && new Date(item.updatedAt).toLocaleDateString().toLowerCase().includes(query))
    );
  });

  // Open delete dialogs
  const openDeleteFormDialog = (id: number, name: string) => {
    setItemToDelete({ id, name });
    setDeleteFormDialog(true);
  };

  const openDeleteDesignationDialog = (id: number, name: string) => {
    setItemToDelete({ id, name });
    setDeleteDesignationDialog(true);
  };

  const openViewFormDialog = async (id: number) => {
    try {
      // Fetch form data, sections, and questions in parallel
      const [formResponse, sectionsResponse, questionsResponse] = await Promise.all([
        getFormById(id),
        CommHandler.request({
          url: `${config.apiBaseUrl}/section`,
          method: 'GET',
          headers: getAuthHeader,
        }),
        CommHandler.request({
          url: `${config.apiBaseUrl}/questions`,
          method: 'GET',
          headers: getAuthHeader,
        }),
      ]);

      if (formResponse && formResponse.data && sectionsResponse?.status === 200 && questionsResponse?.status === 200) {
        // Extract sections data (handle both nested and flat structures)
        const sectionsData = sectionsResponse.data?.data || sectionsResponse.data || [];
        const questionsData = questionsResponse.data || [];

        // Structure the data as expected by ViewFormDialog
        const viewData = {
          form: formResponse.data.form,
          sections: Array.isArray(sectionsData) ? sectionsData : [],
          questions: Array.isArray(questionsData) ? questionsData : [],
        };

        setViewFormData(viewData);
        setViewFormDialog(true);
      } else {
        showErrorSnackbar('Failed to load form data');
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      showErrorSnackbar('Error loading form data');
    }
  };

  // Close dialog handlers
  const closeDeleteFormDialog = () => {
    setDeleteFormDialog(false);
    setItemToDelete(null);
  };

  const closeDeleteDesignationDialog = () => {
    setDeleteDesignationDialog(false);
    setItemToDelete(null);
  };

  const closeViewFormDialog = () => {
    setViewFormDialog(false);
    setViewFormData(null);
  };

  // Delete handlers
  const handleDeleteForm = async () => {
    if (!itemToDelete) return;

    try {
      setIsLoading(true);

      const endpoint = `${config.apiBaseUrl}/evaluation/form/${itemToDelete.id}`;

      await CommHandler.request({
        url: endpoint,
        method: 'DELETE',
        headers: getAuthHeader,
      });

      // Success case - CommHandler returns response data, not the full response object
      showSuccessSnackbar('Form deleted successfully');

      // Remove form from state
      const updatedForms = formData.filter((form) => form.id !== itemToDelete.id);
      setFormData(updatedForms);
    } catch (error) {
      // Error is already handled by CommHandler's response interceptor
      // which shows the backend error message automatically
      console.error('Form deletion error:', error);
    } finally {
      setIsLoading(false);
      closeDeleteFormDialog();
    }
  };

  // Action handlers for designation
  const handleDeleteDesignation = async () => {
    if (!itemToDelete) return;

    try {
      setIsLoading(true);

      const endpoint = `${config.apiBaseUrl}/evaluation/designation-group/${itemToDelete.id}`;

      await CommHandler.request({
        url: endpoint,
        method: 'DELETE',
        headers: getAuthHeader,
      });

      // Success case - CommHandler returns response data, not the full response object
      showSuccessSnackbar('Designation group deleted successfully');

      // Remove designation from state
      const updatedDesignations = designationData.filter((designation) => designation.id !== itemToDelete.id);
      setDesignationData(updatedDesignations);
      setDesignationTotalCount((prev) => prev - 1); // Decrease total count
    } catch (error) {
      // Error is already handled by CommHandler's response interceptor
      // which shows the backend error message automatically
      console.error('Designation deletion error:', error);
    } finally {
      setIsLoading(false);
      closeDeleteDesignationDialog();
    }
  };

  // Added functionality to open the AddEditDesignationForm with prefilled data and save changes via PUT API
  // (removed duplicate handleEditDesignation for inline edit)

  return (
    <StyledEvaluation>
      <Box
        sx={{
          p: '24px 0px 0px 0px',
          width: '100%',
          margin: '0 auto',
          marginLeft: '15px',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Tabs and Button Header */}
        <Box
          sx={{
            mb: 0,
            mt: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 0,
              pt: 0,
              mt: 0,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="evaluation tabs"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                  height: 3,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 'bold',
                },
              }}
            >
              <Tab label="Designation Groups" {...a11yProps(0)} sx={{ textTransform: 'none', fontSize: '16px', px: 5, mr: 2 }} />
              {hasSlabMappingPermission && <Tab label="Slab Mapping" {...a11yProps(1)} sx={{ textTransform: 'none', fontSize: '16px', px: 5, ml: 2 }} />}
              <Tab label="Forms" {...a11yProps(2)} sx={{ textTransform: 'none', fontSize: '16px', px: 5, mx: 2 }} />
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {(tabValue === 0 || tabValue === 2) && (
                <TextField
                  size="small"
                  placeholder="Search"
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12.5 11H11.71L11.43 10.73C12.41 9.59 13 8.11 13 6.5C13 2.91 10.09 0 6.5 0C2.91 0 0 2.91 0 6.5C0 10.09 2.91 13 6.5 13C8.11 13 9.59 12.41 10.73 11.43L11 11.71V12.5L16 17.49L17.49 16L12.5 11ZM6.5 11C4.01 11 2 8.99 2 6.5C2 4.01 4.01 2 6.5 2C8.99 2 11 4.01 11 6.5C11 8.99 8.99 11 6.5 11Z"
                            fill="#757575"
                          />
                        </svg>
                      </Box>
                    ),
                    sx: {
                      borderRadius: '4px',
                      fontSize: '14px',
                      height: '36px',
                      width: '220px',
                    },
                  }}
                />
              )}
              {((tabValue === 0 && hasDGAddPermission) || (tabValue === 2 && hasFormAddPermission)) && (
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleAddClick}
                  sx={{
                    bgcolor: '#1976d2',
                    '&:hover': { bgcolor: '#1565c0' },
                    textTransform: 'none',
                    borderRadius: '4px',
                    px: 2,
                    color: '#ffffff',
                  }}
                >
                  {getAddButtonText()}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Designation Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer sx={{ width: '100%', p: 0, mt: 0 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#e0ebf5' }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      pl: 3,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('groupName')}
                  >
                    NAME
                    <SortIcon column="groupName" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', fontSize: '14px', color: '#5A6473', py: 1.5 }}>DEPARTMENT</TableCell>
                  <TableCell sx={{ fontWeight: '600', fontSize: '14px', color: '#5A6473', py: 1.5 }}>NUMBER OF DESIGNATIONS</TableCell>
                  <TableCell sx={{ fontWeight: '600', fontSize: '14px', color: '#5A6473', py: 1.5 }}>UPDATED ON</TableCell>
                  <TableCell sx={{ fontWeight: '600', fontSize: '14px', color: '#5A6473', py: 1.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && filteredDesignationData.length > 0 && (
                  <>
                    {filteredDesignationData.map((row) => (
                      <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ pl: 3, overflowWrap: 'anywhere' }}>{truncateText(row.groupName, 50)}</TableCell>
                        <TableCell>{row.department}</TableCell>
                        <TableCell>{row.designations}</TableCell>
                        <TableCell>
                          {new Date(row.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </TableCell>
                        <TableCell sx={{ pr: 3 }}>
                          <Box sx={{ display: 'flex' }}>
                            {hasDGEditPermission && (
                              <IconButton
                                sx={{ color: '#1976d2', padding: '4px', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } }}
                                size="small"
                                aria-label="Edit designation"
                                title="Edit"
                                onClick={async () => {
                                  try {
                                    setIsLoading(true);
                                    // Fetch full designation details including designations
                                    const response = await CommHandler.request({
                                      url: `${config.apiBaseUrl}/evaluation/designation-group/${row.id}`,
                                      method: 'GET',
                                      headers: getAuthHeader,
                                    });

                                    if (response?.status === 200) {
                                      const fullData = response.data;

                                      // Extract all designation IDs from all departments
                                      let designationIds: string[] = [];
                                      if (fullData.departments) {
                                        fullData.departments.forEach((dept: any) => {
                                          if (dept.designations && dept.designations.length > 0) {
                                            designationIds = [...designationIds, ...dept.designations.map((d: any) => d.id || d)];
                                          }
                                        });
                                      }

                                      // Create a proper state object with all required data
                                      navigate(`/evaluation/designation/update`, {
                                        state: {
                                          data: {
                                            id: fullData.id,
                                            groupName: fullData.name, // Use original name, not combined
                                            level: fullData.level,
                                            department: row.department,
                                            department_id: fullData.department_id,
                                            // Pass the actual designation IDs
                                            designations: designationIds,
                                          },
                                        },
                                      });
                                    } else {
                                      showErrorSnackbar('Failed to fetch designation details');
                                      navigate('/evaluation/designation/create', { state: { data: { ...row, groupName: row.originalName } } });
                                    }
                                  } catch (err) {
                                    showErrorSnackbar('Error fetching designation details');
                                    navigate('/evaluation/designation/create', { state: { data: { ...row, groupName: row.originalName } } });
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {hasDGDeletePermission && designationTotalCount > 1 && (
                              <IconButton
                                sx={{
                                  color: '#d32f2f',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                                }}
                                size="small"
                                aria-label="Delete designation"
                                title="Delete"
                                onClick={() => openDeleteDesignationDialog(row.id, row.groupName)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
                {!isLoading && filteredDesignationData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No designations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={designationTotalCount}
                rowsPerPage={designationRowsPerPage}
                page={designationPage}
                onPageChange={(e, newPage) => {
                  setDesignationPage(newPage);
                }}
                onRowsPerPageChange={(e) => {
                  setDesignationRowsPerPage(parseInt(e.target.value, 10));
                  setDesignationPage(0);
                }}
                showFirstButton
                showLastButton
              />
            </Box>
          </TableContainer>
        </TabPanel>

        {/* Slab Mapping Tab */}
        {hasSlabMappingPermission && (
          <TabPanel value={tabValue} index={1}>
            <SlabMapping />
          </TabPanel>
        )}

        {/* Forms Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer sx={{ width: '100%', p: 0, mt: 0 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#e0ebf5' }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      pl: 3,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('name')}
                  >
                    FORM NAME
                    <SortIcon column="name" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('category')}
                  >
                    CATEGORY
                    <SortIcon column="category" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('designationGroup')}
                  >
                    DESIGNATION GROUP
                    <SortIcon column="designationGroup" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('questions')}
                  >
                    NUMBER OF QUESTIONS
                    <SortIcon column="questions" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#5A6473',
                      py: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleSort('updatedAt')}
                  >
                    UPDATED ON
                    <SortIcon column="updatedAt" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: '600', fontSize: '14px', color: '#5A6473', py: 1.5, width: 120, pr: 3 }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && filteredFormData.length > 0 && (
                  <>
                    {filteredFormData.map((row) => (
                      <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {truncateText(row.name, 50)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.category}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {truncateText(row.designationGroup, 50)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.questions}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(row.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <IconButton
                              sx={{
                                color: '#1976d2',
                                padding: '4px',
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                              }}
                              size="small"
                              aria-label="View form"
                              title="View"
                              onClick={() => openViewFormDialog(row.id)}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                            {hasFormEditPermission && (
                              <IconButton
                                sx={{
                                  color: '#1976d2',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                                }}
                                size="small"
                                aria-label="Edit form"
                                title="Edit"
                                onClick={async () => {
                                  // Fetch full form details if needed, else just pass row
                                  try {
                                    const response = await CommHandler.request({
                                      url: `${config.apiBaseUrl}/evaluation/form/${row.id}`,
                                      method: 'GET',
                                      headers: getAuthHeader,
                                    });
                                    if (response.status === 200 && response.data) {
                                      // Ensure approver information is properly formatted
                                      const editFormData = {
                                        ...response.data,
                                        form: {
                                          ...response.data.form,
                                          // Add approver name for autofill
                                          approverName: response.data.form.approverUser?.fullName || '',
                                          approverEmail: response.data.form.approverUser?.email || '',
                                          // Keep the original approver ID
                                          approverId: response.data.form.approver,
                                        },
                                      };
                                      navigate('/evaluation/update-form', { state: { data: editFormData } });
                                    } else {
                                      showErrorSnackbar('Failed to load form details');
                                    }
                                  } catch (err) {
                                    showErrorSnackbar('Failed to load form details');
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {hasFormDeletePermission && (
                              <IconButton
                                sx={{
                                  color: '#d32f2f',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                                }}
                                size="small"
                                aria-label="Delete form"
                                title="Delete"
                                onClick={() => openDeleteFormDialog(row.id, row.name)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
                {!isLoading && filteredFormData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No forms found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={formTotalCount}
                rowsPerPage={formRowsPerPage}
                page={formPage}
                onPageChange={(e, newPage) => {
                  setFormPage(newPage);
                }}
                onRowsPerPageChange={(e) => {
                  setFormRowsPerPage(parseInt(e.target.value, 10));
                  setFormPage(0);
                }}
                showFirstButton
                showLastButton
              />
            </Box>
          </TableContainer>
        </TabPanel>
      </Box>

      {/* Delete confirmation dialogs */}
      {/* Designation Delete Dialog */}
      <Dialog
        open={deleteDesignationDialog}
        onClose={closeDeleteDesignationDialog}
        aria-labelledby="delete-designation-dialog-title"
        aria-describedby="delete-designation-dialog-description"
        PaperProps={{
          sx: {
            width: '400px',
            borderRadius: '8px',
            p: 1,
          },
        }}
      >
        <DialogTitle id="delete-designation-dialog-title" sx={{ fontWeight: 600 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-designation-dialog-description">Are you sure you want to delete the designation group &quot;{itemToDelete?.name}&quot;? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDeleteDesignationDialog}
            variant="outlined"
            sx={{
              color: '#1976d2',
              borderColor: '#1976d2',
              textTransform: 'none',
              minWidth: '80px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDesignation}
            variant="contained"
            color="error"
            autoFocus
            sx={{
              textTransform: 'none',
              minWidth: '80px',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Delete Dialog */}
      <Dialog
        open={deleteFormDialog}
        onClose={closeDeleteFormDialog}
        aria-labelledby="delete-form-dialog-title"
        aria-describedby="delete-form-dialog-description"
        PaperProps={{
          sx: {
            width: '400px',
            borderRadius: '8px',
            p: 1,
          },
        }}
      >
        <DialogTitle id="delete-form-dialog-title" sx={{ fontWeight: 600 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-form-dialog-description">Are you sure you want to delete the form &quot;{itemToDelete?.name}&quot;? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDeleteFormDialog}
            variant="outlined"
            sx={{
              color: '#1976d2',
              borderColor: '#1976d2',
              textTransform: 'none',
              minWidth: '80px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteForm}
            variant="contained"
            color="error"
            autoFocus
            sx={{
              textTransform: 'none',
              minWidth: '80px',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Form Dialog */}
      <ViewFormDialog open={viewFormDialog} onClose={closeViewFormDialog} formData={viewFormData} />
    </StyledEvaluation>
  );
};

export default Evaluation;
